import type { Document } from "../api/schemas";

export type JourneyChannel = "mobile" | "telegram";
export type JourneyStageKey = "registered" | "active" | "documentUpload";

/**
 * The per-user shape this funnel needs — `userRef`, `channel`, and
 * `registeredAt` only. Deliberately **not** the `GET /users` wire type: that
 * endpoint returns a bare count, not a roster, as of ISSUE-455 Deliverable 3
 * (ADR-052 §3 amended 2026-09-18). This funnel stays fixture-only
 * (`dashboard/fixtures.ts`'s `journeyUsersFixture`) — a per-user roster to
 * drive it live is not a data source `GET /users` can ever provide again.
 */
export interface JourneyUser {
  readonly userRef: string;
  readonly channel: JourneyChannel;
  /** `null` for Telegram — there is no Telegram registration event (ADR-052 §3). */
  readonly registeredAt: string | null;
}

/**
 * A stage's count for one channel. `null` means the channel has no
 * observable event for this stage at all (ADR-052 §3) — never a fabricated
 * zero standing in for "this channel doesn't capture that."
 */
export type JourneyStageCount = number | null;

export interface JourneyStage {
  readonly key: JourneyStageKey;
  readonly label: string;
  readonly mobile: JourneyStageCount;
  readonly telegram: JourneyStageCount;
  /** Present only when a channel's count can be `null` for this stage — why, in plain language. */
  readonly unavailableReason?: string;
}

function countByChannel(
  users: readonly JourneyUser[],
  channel: JourneyChannel,
  predicate: (user: JourneyUser) => boolean,
): number {
  return users.filter((user) => user.channel === channel && predicate(user)).length;
}

/**
 * A channel's registration count is unavailable — not zero — only when
 * every one of its users carries no registration event (ADR-052 §3:
 * Telegram has no Entra identity, so `registeredAt` is always `null`).
 * Derived from the data rather than hardcoded to a channel name, so it
 * still holds if the asymmetry itself changes.
 */
function hasNoRegistrationEvent(users: readonly JourneyUser[], channel: JourneyChannel): boolean {
  const channelUsers = users.filter((user) => user.channel === channel);
  return channelUsers.length > 0 && channelUsers.every((user) => user.registeredAt === null);
}

function registeredCount(users: readonly JourneyUser[], channel: JourneyChannel): JourneyStageCount {
  return hasNoRegistrationEvent(users, channel)
    ? null
    : countByChannel(users, channel, (user) => user.registeredAt !== null);
}

function documentUploadCount(
  users: readonly JourneyUser[],
  documents: readonly Document[],
  channel: JourneyChannel,
): number {
  const ownersWithDocuments = new Set(documents.map((document) => document.ownerRef));
  return countByChannel(users, channel, (user) => ownersWithDocuments.has(user.userRef));
}

/**
 * Registration → session (last activity) → document-upload funnel (ISSUE-352
 * AC1), computed from `JourneyUser` rows and the `GET /documents` shape
 * `DocumentsView` already consumes. Honors the channel-asymmetry rendering
 * rule ISSUE-329 AC3 established: a stage with no observable event for a
 * channel is `null`, never `0`.
 */
export function computeJourneyStages(
  users: readonly JourneyUser[],
  documents: readonly Document[],
): readonly JourneyStage[] {
  return [
    {
      key: "registered",
      label: "Registered",
      mobile: registeredCount(users, "mobile"),
      telegram: registeredCount(users, "telegram"),
      unavailableReason: "Telegram has no Entra identity, so there is no registration event to count.",
    },
    {
      key: "active",
      label: "Active (last activity)",
      mobile: countByChannel(users, "mobile", () => true),
      telegram: countByChannel(users, "telegram", () => true),
    },
    {
      key: "documentUpload",
      label: "Uploaded a document",
      mobile: documentUploadCount(users, documents, "mobile"),
      telegram: documentUploadCount(users, documents, "telegram"),
    },
  ];
}
